import React, { useState } from 'react';
import { getStoredInviteKey, hasInviteLinkError } from './inviteKey';
import './ClerkBetaAdmissionScreen.css';

/**
 * Clerk-native beta admission.
 *
 * This screen appears ONLY after a Clerk session already exists and the backend
 * answered `403 beta_key_required` — i.e. the Clerk identity does not yet map to a
 * Django user and provisioning needs an admission key.
 *
 * It is NOT the legacy `BetaKeyValidation -> UserRegistration` flow and must never
 * route into it. Legacy registration is the pre-Clerk path; sending a Clerk-
 * authenticated user there would create a second, unmapped account.
 *
 * Rules enforced here:
 * - The Clerk session is preserved. Reaching this screen never signs the user out.
 * - The key is held in local component state only. It is never logged, and it is
 *   never written to storage by this component.
 * - Submitting is always an explicit user action. A shared key's `usage_count` is
 *   a limited, revocable resource, so nothing here auto-submits a prefilled key.
 * - Validity, `INDIVIDUAL` target-email matching, and atomic `SHARED` consumption
 *   are decided server-side under lock. This component does no pre-checking and
 *   does not branch on key format.
 *
 * Copy is provisional and pending owner approval.
 */
function ClerkBetaAdmissionScreen({ onSubmit, isSubmitting = false, errorType = null, onSignOut }) {
    if (typeof onSubmit !== 'function') {
        throw new Error(
            `ClerkBetaAdmissionScreen requires an onSubmit function; received ${typeof onSubmit}.`
        );
    }

    // Prefill from a QR/shared invite when this tab captured one. Prefill only --
    // the user still presses the button, so a crawled invite link cannot burn a seat.
    const [betaKey, setBetaKey] = useState(() => getStoredInviteKey() || '');
    const [inviteLinkFailed] = useState(() => hasInviteLinkError());

    const trimmedKey = betaKey.trim();
    const canSubmit = trimmedKey.length > 0 && isSubmitting !== true;

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!canSubmit) return;
        onSubmit(trimmedKey);
    };

    return (
        <main className="clerk-admission" aria-label="Beta access required">
            <form className="clerk-admission__panel" onSubmit={handleSubmit}>
                <h1 className="clerk-admission__title">Enter your beta key</h1>
                <p className="clerk-admission__message">
                    You&apos;re signed in. Mythic is currently in closed beta, so you&apos;ll need
                    an invite key to finish setting up your account.
                </p>

                {inviteLinkFailed && (
                    <p className="clerk-admission__notice" role="status">
                        That invite link didn&apos;t look right, so we couldn&apos;t fill your key
                        in automatically. Please enter it below.
                    </p>
                )}

                {errorType === 'beta_key_invalid' && (
                    <p className="clerk-admission__error" role="alert">
                        That key isn&apos;t valid. Check it and try again.
                    </p>
                )}

                <label className="clerk-admission__label" htmlFor="clerk-beta-key">
                    Beta key
                </label>
                <input
                    id="clerk-beta-key"
                    className="clerk-admission__input"
                    type="text"
                    value={betaKey}
                    onChange={(event) => setBetaKey(event.target.value)}
                    autoComplete="off"
                    spellCheck="false"
                    disabled={isSubmitting === true}
                />

                <button
                    type="submit"
                    className="clerk-admission__submit"
                    disabled={!canSubmit}
                >
                    {isSubmitting === true ? 'Checking…' : 'Continue'}
                </button>

                {typeof onSignOut === 'function' && (
                    <button
                        type="button"
                        className="clerk-admission__secondary"
                        onClick={onSignOut}
                        disabled={isSubmitting === true}
                    >
                        Sign out
                    </button>
                )}
            </form>
        </main>
    );
}

export default ClerkBetaAdmissionScreen;
